import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import DndDroppable from 'components/dnd/DndDroppable.component';
import { selectSidebarOpen } from 'selectors/sidebar.selectors';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import { formatHourAndMinuteShortMeridiem } from 'utils/date/format.util';
import { isEnvMobile } from 'utils/env/determine.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';
import dnd from 'utils/react/dnd.util';

import CN from './SchedulerCell.module.scss';

const L = logger('SchedulerCell');

const DND_TARGET = 'SchedulerCellWeek--dnd-target';

const determineExtraProps = ({ stylist, time, isInWorkingHour, hovered, dragOverTimeEl, isDragging }) => {
  const stylistId = stylist.id;

  const displayTime = formatHourAndMinuteShortMeridiem(time);
  const formattedTimeForKey = time.format('x'); // FIXME: use formatting utilities

  const contentId = JSON.stringify({ time });
  const cellId = `${stylistId}-${formattedTimeForKey}`;

  const isVisible = (hovered && !dragOverTimeEl && !isDragging) || !hovered;

  return { displayTime, contentId, isInWorkingHour, isVisible, cellId, stylistId };
};

// FIXME: two elements on the same page should not have the same ID
const SchedulerCellContent = ({ children, displayTime, contentId }) => {
  const isMobile = isEnvMobile({ regex: true });
  return (
    <div
      data-bem="SchedulerCellContent"
      className={cn({
        'rollover-container': true,
        'no-hover': isMobile,
        'on-hover': !isMobile,
      })}
    >
      {children}
      <div data-bem="SchedulerCellContent__pull" className="drag-pull" id={contentId} data-time={contentId}>
        <span id={contentId} data-time={contentId} className="placeholder-time">
          {displayTime}
        </span>
      </div>
    </div>
  );
};

const SchedulerCellDay = ({
  children,
  displayTime,
  contentId,
  isInWorkingHour,
  cellId,
  onClick,
  pullClasses,
  pullStyle,
}) => (
  <div
    data-bem="SchedulerCellDay"
    onKeyDown={alwaysNoop}
    role="button"
    tabIndex={0}
    key={cellId}
    data-cy={cellId}
    className={cn({
      [CN.component]: true,
      [CN.isDay]: true,
      'scheduler_item for-column': true,
      'non-working-hour': isInWorkingHour,
    })}
    onClick={onClick}
  >
    <SchedulerCellContent pullClasses={pullClasses} style={pullStyle} contentId={contentId} displayTime={displayTime}>
      {children}
    </SchedulerCellContent>
  </div>
);

const SchedulerCellWeek = ({
  children,
  displayTime,
  contentId,
  isInWorkingHour,
  cellId,
  onClick,
  pullClasses,
  pullStyle,
  time,
  stylistId,
}) => (
  <DndDroppable
    tag={DND_TARGET}
    droppableId={dnd.dropCell({ tag: DND_TARGET, stylist: { id: stylistId }, time })}
    type="SchedulerCell"
    index={0}
  >
    {({ droppableProps, innerRef, placeholder }) => (
      <div
        data-bem="SchedulerCellWeek--root--el-target"
        key={cellId}
        data-cy={cellId}
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={alwaysNoop}
        className={cn({
          [CN.component]: true,
          [CN.isWeek]: true,
          'scheduler_item for-column': true,
          'non-working-hour': isInWorkingHour,
        })}
      >
        <div
          data-bem="SchedulerCellWeek__placeholder--el-target"
          ref={innerRef}
          {...droppableProps}
          className={CN.placeholder}
        >
          {placeholder}
        </div>
        <SchedulerCellContent
          pullClasses={pullClasses}
          style={pullStyle}
          contentId={contentId}
          displayTime={displayTime}
        >
          {children}
        </SchedulerCellContent>
      </div>
    )}
  </DndDroppable>
);

// This component acts as a facade for the two kinds of cells
const SchedulerCell = (props) => {
  const { onClick, droppable, isSidebarOpen } = props;

  const extra = determineExtraProps(props);
  const isWeek = fromAnyToBool(droppable);

  const handleClick = useCallback(
    (ev) => {
      if (isSidebarOpen) {
        // NOTE: scheduler blocks don't handle clicks nicely while the sidebar is open, so ignore the click event
        // SEE: https://getslick.atlassian.net/browse/GS-11963
        L.info('handleClick()', 'Sidebar is open, ignoring click on', ev.target);
        return void 1;
      }

      return call(`SchedulerCell.onClick`, onClick, ev);
    },
    [isSidebarOpen, onClick],
  );

  return isWeek ? (
    <SchedulerCellWeek {...props} {...extra} key={extra.cellId} onClick={handleClick} />
  ) : (
    <SchedulerCellDay {...props} {...extra} key={extra.cellId} onClick={handleClick} />
  );
};

const mapStateToProps = (state) => ({
  isSidebarOpen: selectSidebarOpen(state),
});

const SchedulerCellHoc = connect(mapStateToProps)(SchedulerCell);

export default SchedulerCellHoc;
